import { useEffect } from 'react';
import { BASE_URL, LANGUAGES, getSeoCopy, localizedPath } from '../config/seo';
import { useLanguage } from '../context/LanguageContext';

const upsertMeta = (selector, attributes) => {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement('meta');
    document.head.appendChild(node);
  }
  Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
};

const upsertLink = (selector, attributes) => {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement('link');
    document.head.appendChild(node);
  }
  Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
};

const graphFor = (lang, page, canonical, unit, overrideTitle, overrideDescription) => {
  const copy = getSeoCopy(lang, page);
  const pageTitle = overrideTitle || copy.title;
  const pageDescription = overrideDescription || copy.description;
  const graph = [
    { '@type': ['Organization', 'RealEstateAgent'], '@id': `${BASE_URL}/#organization`, name: 'TRES IMMO GMBH', url: BASE_URL, email: 'info@tres.immo', telephone: '+393935652804' },
    { '@type': 'WebSite', '@id': `${BASE_URL}/#website`, name: 'TRES IMMO', url: BASE_URL, inLanguage: lang, publisher: { '@id': `${BASE_URL}/#organization` } },
    { '@type': 'WebPage', '@id': `${canonical}#webpage`, url: canonical, name: pageTitle, description: pageDescription, inLanguage: lang, isPartOf: { '@id': `${BASE_URL}/#website` } },
    { '@type': 'BreadcrumbList', '@id': `${canonical}#breadcrumbs`, itemListElement: [{ '@type': 'ListItem', position: 1, name: 'TRES IMMO', item: `${BASE_URL}${localizedPath(lang, '/')}` }, ...(page === 'home' ? [] : [{ '@type': 'ListItem', position: 2, name: pageTitle, item: canonical }])] },
  ];
  if (page === 'home') graph.push({ '@type': 'ApartmentComplex', '@id': `${canonical}#project`, name: 'Wohnen am Waldrand – Oberbozen', description: pageDescription, url: canonical, numberOfAccommodationUnits: 11, address: { '@type': 'PostalAddress', addressLocality: 'Oberbozen', addressRegion: 'Südtirol', postalCode: '39054', addressCountry: 'IT' }, geo: { '@type': 'GeoCoordinates', latitude: 46.5178, longitude: 11.4467 } });
  if (unit) graph.push({ '@type': 'Apartment', '@id': `${canonical}#apartment`, name: `${unit.id} – Am Waldrand Oberbozen`, url: canonical, floorSize: { '@type': 'QuantitativeValue', value: Number(String(unit.area).replace(/[^0-9,]/g, '').replace(',', '.')), unitCode: 'MTK' }, numberOfRooms: String(unit.rooms).replace(/[^0-9]/g, ''), offers: { '@type': 'Offer', priceCurrency: 'EUR', price: String(unit.price).replace(/[^0-9]/g, '') || undefined, availability: unit.status === 'sold' ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock' } });
  return { '@context': 'https://schema.org', '@graph': graph };
};

export default function SeoHead({ page = 'home', noindex = false, title, description, path, faqItems = [], unit }) {
  const { lang } = useLanguage();
  useEffect(() => {
    const copy = getSeoCopy(lang, page);
    const finalTitle = title || copy.title;
    const finalDescription = description || copy.description;
    const cleanPath = path || (page === 'home' ? '/' : `/${page}`);
    const canonical = `${BASE_URL}${localizedPath(lang, cleanPath)}`;
    document.title = finalTitle;
    document.documentElement.lang = lang;
    upsertMeta('meta[name="description"]', { name: 'description', content: finalDescription });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: noindex ? 'noindex, nofollow' : 'index, follow' });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: finalTitle });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: finalDescription });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: lang === 'de' ? 'de_DE' : lang === 'it' ? 'it_IT' : 'en_US' });
    upsertLink('link[rel="canonical"]', { rel: 'canonical', href: canonical });
    LANGUAGES.forEach((language) => upsertLink(`link[rel="alternate"][hreflang="${language}"]`, { rel: 'alternate', hreflang: language, href: `${BASE_URL}${localizedPath(language, cleanPath)}` }));
    upsertLink('link[rel="alternate"][hreflang="x-default"]', { rel: 'alternate', hreflang: 'x-default', href: `${BASE_URL}${localizedPath('de', cleanPath)}` });
    document.head.querySelectorAll('script[data-tres-seo], script[data-tres-static-seo]').forEach((node) => node.remove());
    const jsonLd = document.createElement('script');
    jsonLd.type = 'application/ld+json';
    jsonLd.dataset.tresSeo = 'true';
    const graph = graphFor(lang, page, canonical, unit, title || copy.title, description || copy.description);
    if (page === 'faq' && faqItems.length) graph['@graph'].push({ '@type': 'FAQPage', '@id': `${canonical}#faq`, mainEntity: faqItems.map(([name, answer]) => ({ '@type': 'Question', name, acceptedAnswer: { '@type': 'Answer', text: answer } })) });
    jsonLd.textContent = JSON.stringify(graph);
    document.head.appendChild(jsonLd);
  }, [description, faqItems, lang, noindex, page, path, title, unit]);
  return null;
}
